// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { SaraIconComponent } from '@shared/ui/sara-icon/sara-icon.component';
import { DataStore } from '@core/store/data.store';

/** Floating yellow "Impersonation ON" rectangle pinned to the bottom-left of
 *  the viewport — renders only while the session runs under an impersonation
 *  override. Bound to the same DataStore signals the HTTP interceptor reads
 *  (`impCode`/`impDomain`), so the indicator can never disagree with the
 *  headers actually sent. */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'impersonation-banner',
  templateUrl: './impersonation-banner.component.html',
  styleUrls: ['./impersonation-banner.component.scss'],
  imports: [SaraIconComponent],
})
export class ImpersonationBannerComponent {
  private readonly ds = inject(DataStore);

  /** Who the session is impersonating: `domain/code` when a pilot code is set,
   *  `DOMAIN: x` when only the customer domain is overridden, null when not
   *  impersonating (banner hidden). Mirrors IPWI's display rules. */
  readonly impersonationTarget = computed(() => {
    const code = this.ds.impCode();
    const domain = this.ds.impDomain();
    if (code) return domain ? `${domain}/${code}` : code;
    return domain ? `DOMAIN: ${domain}` : null;
  });

  /** Tenant analysts hold a curated allow-list and can switch pilot by tapping
   *  the banner; a super user impersonates arbitrary codes via URL (no
   *  allow-list), so their banner stays a passive indicator. */
  readonly canReselect = computed(() => (this.ds.impersonationTargets()?.length ?? 0) > 0);

  /** Re-open the "Choose a pilot" picker. No-op for a super user (guarded in the store). */
  reselect(): void {
    this.ds.reopenImpersonationPicker();
  }
}
